/**
 * Historian Data Viewer Context Provider (djin-cloud#806)
 *
 * Ported from genieos/web/src/contexts/HistorianContext.tsx @ 84923d036, with
 * the entire data layer replaced. What carried over is the SHAPE of the thing:
 * a reducer holding variable selection, time range, interval and Y-axis
 * scaling, exposed through a memoized context value. What did not carry over is
 * every source of data.
 *
 * GenieOS browses a TDengine SCHEMA — supertables, child tables, columns — and
 * posts a `variables[]` query naming a supertable and a register. This repo has
 * no TDengine and no schema endpoint. The cloud tree is
 * SITE → DEVICE → METRIC, sourced from `GET /v1/sites` plus a per-site
 * `GET /v1/historian/catalog/:siteId`, and queries go to
 * `POST /v1/historian/series-query`. So `Column`, `DeviceInfo`, `Supertable`,
 * `Schema`, `DeviceLifecycle`, the `SCHEMA_*` actions and `loadSchema` are all
 * deleted rather than adapted — there is nothing here for them to describe.
 * The `'raw'` pseudo-interval and its high-speed guard are gone for the same
 * reason: they are a TDengine handler feature with no cloud equivalent.
 *
 * ⚠️ Three things in here are load-bearing and easy to "simplify" back into
 * bugs — each is commented at its definition:
 *   1. Identity, alias, colour and axis are derived IN THE REDUCER, not in the
 *      action creator.
 *   2. A preset time range is re-derived at EXECUTE time.
 *   3. Every sessionStorage access is wrapped, and the page must render without it.
 */

import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useMemo,
  type ReactNode,
} from 'react';
import api from '@/services/api';
import {
  fetchCatalog,
  runSeriesQuery,
  fetchPersistedQuery,
  extractHistorianError,
  type HistorianCatalog,
  type HistorianErrorBody,
  type SeriesColumnMeta,
  type SeriesQuerySpec,
  type SeriesQueryResult,
} from '@/services/historianApi';
import { sanitizeAlias, resolveUniqueAlias } from '@/utils/historianAlias';
import { METRIC_UNITS } from '@/utils/historianMetrics';
import { isInterval, type HistorianSeed, type HistorianSeriesSeed } from '@/utils/historianUrlParams';
import {
  CHART_COLORS,
  EXPLICIT_INTERVALS,
  intervalAllowsSpan,
  HISTORIAN_MAX_SERIES,
  TIME_RANGE_PRESETS,
  type IntervalOption,
  type TimeRangePreset,
} from '@/components/historian/historianConstants';
import type {
  YAxisScaleMode,
  YAxisScaleSetting,
  YAxisScales,
} from '@/components/historian/historianTypes';

/**
 * Re-exported, NOT redefined. `YAxisScaleChip` (PR 3) needed this contract
 * before this file existed, so it lives in `historianTypes.ts` and is surfaced
 * here for consumers that reach for it through the context. There must stay
 * exactly one definition — do not paste these back in.
 */
export type { YAxisScaleMode, YAxisScaleSetting, YAxisScales };
export type { IntervalOption, TimeRangePreset };

// ============================================================================
// Types
// ============================================================================

/** A site as the variable browser needs it — the projection of `GET /v1/sites`. */
export interface HistorianSite {
  siteId: string;
  siteName: string;
  siteCode: string;
}

/**
 * A selected variable: one site × device × metric, with its presentation.
 */
export interface SelectedVariable {
  /**
   * `${siteId}:${deviceId}:${metric}`.
   *
   * ⚠️ The `siteId` prefix is not decoration. Device ids are unique per site,
   * not across the fleet — two sites can both have a `GEN-1` — so a
   * device-scoped id would silently dedup one site's series away.
   */
  id: string;
  siteId: string;
  siteName: string;
  deviceId: string;
  deviceLabel: string;
  metric: string;
  /**
   * The wire alias: the `series[].alias` sent to the server, the key in the
   * returned row objects, and the chart `dataKey`. Sanitized to
   * `^[A-Za-z0-9_.-]{1,64}$` and possibly carrying a uniqueness suffix, so it
   * is NOT fit to render — use `displayLabel`.
   */
  alias: string;
  /** Human-readable, unsanitized and untruncated. What the UI shows. */
  displayLabel?: string;
  color: string;
  /**
   * `null` until resolved. The catalog supplies it where it can; otherwise
   * `executeQuery` back-fills from the response's `columnMeta`, then from the
   * static `METRIC_UNITS` catalog. See the unit chain in `executeQuery`.
   */
  unit: string | null;
  yAxisId: 'left' | 'right' | 'right2';
}

/** What `addVariable` is called with — the derived fields are the reducer's job. */
export interface AddVariableRequest {
  siteId: string;
  siteName: string;
  deviceId: string;
  deviceLabel: string;
  metric: string;
  unit: string | null;
}

export interface TimeRange {
  preset: TimeRangePreset;
  start: Date;
  end: Date;
}

export interface QueryMetadata {
  pointCount: number;
  queryTimeMs: number;
  downsampledFrom: number;
  interval: string;
  truncated: boolean;
  /** Which end the row budget cut — always 'oldest' when set (#812). Null when nothing was cut. */
  truncatedSide: 'oldest' | null;
  /** ISO instant from which every requested series is present. Null when nothing was cut. */
  completeFrom: string | null;
  /** ISO instant the series stops being complete, when a rollup was too stale to tail-fill (#832). */
  dataThrough: string | null;
  /** Worst rollup lag across the fan-out, in seconds. */
  rollupLagSeconds: number | null;
  start: string;
  end: string;
}

export interface QueryResult {
  queryId: string;
  data: Array<Record<string, number | null>>;
  columnMeta: Record<string, SeriesColumnMeta>;
  metadata: QueryMetadata;
}

/**
 * Auto on every axis. Frequency, power factor and SOC are unreadable without
 * it, and the noise floor plus the on-screen span badge are what keep it from
 * misleading on the signals where zero matters.
 */
export const DEFAULT_Y_AXIS_SCALES: YAxisScales = {
  left: { mode: 'auto' },
  right: { mode: 'auto' },
  right2: { mode: 'auto' },
};

export interface HistorianState {
  sites: HistorianSite[];
  sitesLoading: boolean;
  sitesError: string | null;

  catalogs: Map<string, HistorianCatalog>;
  catalogLoading: Set<string>;
  catalogErrors: Map<string, string>;

  selectedVariables: SelectedVariable[];
  expandedSites: Set<string>;
  expandedDevices: Set<string>;
  /**
   * Category groups the user has flipped from their DEFAULT state (djin-cloud#954).
   * Not "expanded": a group's default depends on its size (see `isGroupOpen` in
   * `VariableBrowser.tsx`), so the set records the toggle and the browser applies it.
   */
  toggledGroups: Set<string>;
  searchQuery: string;

  timeRange: TimeRange;
  interval: IntervalOption;
  yAxisScales: YAxisScales;

  queryLoading: boolean;
  /** The STRUCTURED body, not a string — the UI renders `code`/`message`/`hint`. */
  queryError: HistorianErrorBody | null;
  queryResult: QueryResult | null;
}

type HistorianAction =
  | { type: 'SITES_LOADING' }
  | { type: 'SITES_SUCCESS'; payload: HistorianSite[] }
  | { type: 'SITES_ERROR'; payload: string }
  | { type: 'CATALOG_LOADING'; payload: string }
  | { type: 'CATALOG_SUCCESS'; payload: { siteId: string; catalog: HistorianCatalog } }
  | { type: 'CATALOG_ERROR'; payload: { siteId: string; message: string } }
  | { type: 'ADD_VARIABLE'; payload: AddVariableRequest }
  /**
   * Replace the whole selection verbatim (capped and deduped below).
   *
   * ⚠️ Dispatched by ALL THREE `seedFromUrl` branches (legacy, explicit
   * `series=`, and persisted-query replay) — deep-link seeding always
   * REPLACES rather than appends; see `seedFromUrl`'s own comment for why.
   * This comment used to say "used only by ... persisted-query replay",
   * which was true when this action was introduced and became false the
   * moment round 1 (#806 review) widened the other two branches to use it
   * as well — exactly the stale-comment defect M-6 fixed elsewhere in this
   * file, caught here on a second pass.
   *
   * Only the persisted-query replay branch is actually BYPASSING
   * `buildVariable`'s alias generation, and it has no alternative: its
   * aliases are NOT renegotiable, they are the exact keys
   * `queryResult.data`/`columnMeta` already use (the original query's own
   * aliases), so re-deriving them via `ADD_VARIABLE` would silently break
   * the alias -> row-key linkage `MultiAxisChart` depends on. The legacy
   * and explicit-series branches build fresh `SelectedVariable`s via
   * `buildVariable` too, then dispatch them here as a REPLACE rather than
   * an append — for them this action changes WHICH array wins, not HOW
   * each entry is built.
   *
   * Not exposed on `HistorianContextType` — internal only.
   */
  | { type: 'SEED_VARIABLES'; payload: SelectedVariable[] }
  | { type: 'REMOVE_VARIABLE'; payload: string }
  | { type: 'CLEAR_VARIABLES' }
  | { type: 'UPDATE_VARIABLE_ALIAS'; payload: { id: string; alias: string } }
  | { type: 'UPDATE_VARIABLE_COLOR'; payload: { id: string; color: string } }
  | { type: 'UPDATE_VARIABLE_UNIT'; payload: { id: string; unit: string | null } }
  | { type: 'TOGGLE_SITE'; payload: string }
  | { type: 'TOGGLE_DEVICE'; payload: string }
  | { type: 'TOGGLE_GROUP'; payload: string }
  | { type: 'SET_SEARCH_QUERY'; payload: string }
  | { type: 'SET_TIME_RANGE'; payload: TimeRange }
  | { type: 'SET_INTERVAL'; payload: IntervalOption }
  | { type: 'SET_Y_AXIS_SCALE'; payload: { axis: 'left' | 'right' | 'right2'; setting: YAxisScaleSetting } }
  | { type: 'QUERY_LOADING' }
  | { type: 'QUERY_SUCCESS'; payload: QueryResult }
  | { type: 'QUERY_ERROR'; payload: HistorianErrorBody }
  | { type: 'CLEAR_QUERY' };

// ============================================================================
// Pure helpers
// ============================================================================

/**
 * Y-axis assignment by unit — logic unchanged from genieos, hoisted out of the
 * provider because the REDUCER needs it (genieos wrapped it in `useCallback`,
 * which a reducer cannot reach).
 */
export function getYAxisForUnit(unit?: string | null): 'left' | 'right' | 'right2' {
  if (!unit) return 'left';
  const normalizedUnit = unit.toLowerCase();

  // Percentage units go on left axis
  if (normalizedUnit === '%' || normalizedUnit === 'percent') return 'left';

  // Voltage and power units go on right axis
  if (['v', 'kv', 'mv', 'kw', 'w', 'mw', 'kvar', 'var', 'a', 'ma', 'ka'].includes(normalizedUnit)) {
    return 'right';
  }

  // Temperature and frequency go on right2 axis
  if (['°c', 'c', '°f', 'f', 'hz', 'khz'].includes(normalizedUnit)) {
    return 'right2';
  }

  // Default to left axis
  return 'left';
}

/**
 * First word of a site name — `'Hondo Microgrid — Hibernia'` → `'Hondo'`.
 * Falls back to the site id so a blank name cannot produce a leading-underscore
 * alias that reads like a missing field.
 */
function siteShort(siteName: string, siteId: string): string {
  const first = siteName.trim().split(/\s+/)[0];
  return first || siteId;
}

/**
 * The ONE place the variable identity template lives.
 *
 * ⚠️ This was written out three times — in `buildVariable`, in the reducer's
 * `ADD_VARIABLE` dedup check, and in `isSelected`. Divergence between any two
 * of them is silent and nasty: the tree checkbox never lights, and re-clicks
 * are deduped against an id that does not match the one stored, so the UI
 * looks broken with nothing in the console. Call this; never retype it.
 *
 * The `siteId` component is not decoration — device ids are unique per site,
 * not across the fleet, so two sites can both have a `GEN-1`.
 */
export function variableId(siteId: string, deviceId: string, metric: string): string {
  return `${siteId}:${deviceId}:${metric}`;
}

function nextColor(inUse: ReadonlySet<string>, count: number): string {
  return CHART_COLORS.find((c) => !inUse.has(c)) ?? CHART_COLORS[count % CHART_COLORS.length];
}

function buildVariable(req: AddVariableRequest, existing: readonly SelectedVariable[]): SelectedVariable {
  const short = siteShort(req.siteName, req.siteId);
  const alias = resolveUniqueAlias(
    sanitizeAlias(`${short}_${req.deviceId}_${req.metric}`),
    new Set(existing.map((v) => v.alias))
  );
  return {
    id: variableId(req.siteId, req.deviceId, req.metric),
    siteId: req.siteId,
    siteName: req.siteName,
    deviceId: req.deviceId,
    deviceLabel: req.deviceLabel,
    metric: req.metric,
    alias,
    displayLabel: `${short} · ${req.deviceLabel} · ${req.metric.replace(/_/g, ' ')}`,
    color: nextColor(new Set(existing.map((v) => v.color)), existing.length),
    unit: req.unit,
    yAxisId: getYAxisForUnit(req.unit),
  };
}

/**
 * Value equality for a time range — the comparison an effect dependency CANNOT
 * make for itself, since it only ever compares object identity.
 *
 * Exported so it can be tested directly. The `end` comparison is unreachable
 * through today's call paths (a `custom` range resolves to its own boundaries,
 * and a duration preset shifts `start` and `end` together), so no
 * integration-level test can discriminate it — measured: a mutant dropping it
 * survived the whole suite. It is still part of this predicate's contract, and
 * a rolling window that pins `start` and advances `end` is an obvious future
 * shape, so it is covered by a unit test rather than deleted as dead.
 */
export function sameRange(a: TimeRange, b: TimeRange): boolean {
  return (
    a.preset === b.preset &&
    a.start.getTime() === b.start.getTime() &&
    a.end.getTime() === b.end.getTime()
  );
}

/**
 * Resolve the window a query should actually run over.
 *
 * ⚠️ A preset is re-derived against the clock HERE, at execute time. The
 * boundaries stored alongside it are display state and go stale the moment the
 * page sits idle — a `last_1h` restored from sessionStorage after lunch must
 * mean the hour before you pressed Execute, not the hour before you picked the
 * preset. `custom` is the only range sent verbatim.
 */
function resolveExecutionRange(range: TimeRange): { start: Date; end: Date } {
  if (range.preset === 'custom') return { start: range.start, end: range.end };
  const preset = TIME_RANGE_PRESETS.find((p) => p.id === range.preset);
  if (!preset) return { start: range.start, end: range.end };
  const end = new Date();
  return { start: new Date(end.getTime() - preset.ms), end };
}

/**
 * How long a range is, for the interval span caps (djin-cloud#952). A preset
 * measures its DURATION rather than its stored boundaries, for the same reason
 * `resolveExecutionRange` re-derives it: the boundaries are display state.
 */
export function rangeSpanMs(range: TimeRange): number {
  const preset = range.preset === 'custom' ? undefined : TIME_RANGE_PRESETS.find((p) => p.id === range.preset);
  return preset ? preset.ms : range.end.getTime() - range.start.getTime();
}

/**
 * The interval the page may hold over `range`: `interval` itself, or `auto`
 * when it is pinned finer than the range allows (`INTERVAL_MAX_SPAN_MS`).
 * Applied on every write of either field, so widening the range past a pinned
 * `1s` falls back to `auto` rather than leaving a selection the server rejects.
 */
function coerceInterval(interval: IntervalOption, range: TimeRange): IntervalOption {
  return intervalAllowsSpan(interval, rangeSpanMs(range)) ? interval : 'auto';
}

// ============================================================================
// Session persistence
// ============================================================================

const STORAGE_KEY = 'historian:v1';

interface PersistedState {
  selectedVariables: SelectedVariable[];
  timeRange: { preset: TimeRangePreset; start: string; end: string };
  interval: IntervalOption;
  yAxisScales: YAxisScales;
}

function defaultState(): HistorianState {
  const end = new Date();
  return {
    sites: [],
    sitesLoading: false,
    sitesError: null,
    catalogs: new Map(),
    catalogLoading: new Set(),
    catalogErrors: new Map(),
    selectedVariables: [],
    expandedSites: new Set(),
    expandedDevices: new Set(),
    toggledGroups: new Set(),
    searchQuery: '',
    timeRange: { preset: 'last_1h', start: new Date(end.getTime() - 3600e3), end },
    interval: 'auto',
    yAxisScales: DEFAULT_Y_AXIS_SCALES,
    queryLoading: false,
    queryError: null,
    queryResult: null,
  };
}

const VALID_INTERVALS = new Set<string>(['auto', ...EXPLICIT_INTERVALS]);
const VALID_PRESETS = new Set<string>(['custom', ...TIME_RANGE_PRESETS.map((p) => p.id)]);
const VALID_AXES = new Set<string>(['left', 'right', 'right2']);
const VALID_SCALE_MODES = new Set<string>(['auto', 'zero', 'manual']);

function isPersistedVariable(v: unknown): v is SelectedVariable {
  if (typeof v !== 'object' || v === null) return false;
  const c = v as Record<string, unknown>;
  return (
    typeof c.id === 'string' &&
    typeof c.siteId === 'string' &&
    typeof c.siteName === 'string' &&
    typeof c.deviceId === 'string' &&
    typeof c.deviceLabel === 'string' &&
    typeof c.metric === 'string' &&
    typeof c.alias === 'string' &&
    typeof c.color === 'string' &&
    (c.unit === null || typeof c.unit === 'string') &&
    typeof c.yAxisId === 'string' &&
    VALID_AXES.has(c.yAxisId)
  );
}

function isPersistedYAxisScales(v: unknown): v is YAxisScales {
  if (typeof v !== 'object' || v === null) return false;
  const c = v as Record<string, unknown>;
  return (['left', 'right', 'right2'] as const).every((axis) => {
    const setting = c[axis];
    if (typeof setting !== 'object' || setting === null) return false;
    return VALID_SCALE_MODES.has(String((setting as { mode?: unknown }).mode));
  });
}

/**
 * ⚠️ `sessionStorage` is the FIRST use of web storage anywhere in
 * `djin-cloud/web`, so nothing else here has ever had to survive its absence.
 * It does not merely return null when unavailable — reading the property
 * THROWS in some privacy modes and in sandboxed iframes. Hence the whole body
 * is wrapped, and a failure returns the defaults rather than propagating: a
 * page that will not render is a far worse outcome than a lost selection.
 *
 * Restored data is UNTRUSTED — it is whatever is in the browser, including a
 * payload written by an older build or edited by hand — so every field is
 * shape-checked before use and `Date`s are revived rather than assumed. The
 * checks are per FIELD, not all-or-nothing: a bad `interval` falls back to its
 * default without discarding a perfectly good selection, and a malformed
 * variable is dropped from the list rather than taking the list with it. A
 * restored variable keeps the `id` it was saved with, so `isSelected` and the
 * `ADD_VARIABLE` dedup still agree with it (see `variableId`).
 */
function loadPersistedState(): HistorianState {
  const base = defaultState();
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return base;
    const parsed = JSON.parse(raw) as Partial<PersistedState>;

    /**
     * ⚠️ The `typeof === 'string'` test is NOT redundant with the
     * `Number.isNaN` check below, however much it looks it. `new Date(null)` is
     * the epoch, `new Date(true)` is epoch+1ms, and `new Date(0)` is the epoch
     * — all valid Dates, none of them NaN. A payload carrying `"start": null`
     * with `"preset": "custom"` therefore restored a range beginning in 1970,
     * and `executeQuery` sends a custom range VERBATIM, so the request spanned
     * ~56 years against the server's 366-day limit. The client-side
     * `MAX_RANGE_DAYS` guard lives in `TimeRangeSelector`'s apply path and
     * never runs on restore, so nothing downstream caught it.
     *
     * Typed `unknown` deliberately: `PersistedState` describes what we WROTE,
     * not what we read back, and narrowing from `string` would let TypeScript
     * present this check as vacuous.
     */
    const rawStart: unknown = parsed.timeRange?.start;
    const rawEnd: unknown = parsed.timeRange?.end;
    const start = typeof rawStart === 'string' ? new Date(rawStart) : null;
    const end = typeof rawEnd === 'string' ? new Date(rawEnd) : null;
    const timeRange =
      start &&
      end &&
      !Number.isNaN(start.getTime()) &&
      !Number.isNaN(end.getTime()) &&
      VALID_PRESETS.has(String(parsed.timeRange!.preset))
        ? { preset: parsed.timeRange!.preset, start, end }
        : base.timeRange;

    return {
      ...base,
      // Filtered, then clamped: a malformed entry is dropped rather than
      // trusted, and a payload carrying more than the cap (an older cap, a
      // hand-edited value) must not walk straight past the limit the reducer
      // enforces.
      selectedVariables: Array.isArray(parsed.selectedVariables)
        ? parsed.selectedVariables.filter(isPersistedVariable).slice(0, HISTORIAN_MAX_SERIES)
        : base.selectedVariables,
      timeRange,
      interval: VALID_INTERVALS.has(String(parsed.interval))
        ? coerceInterval(parsed.interval as IntervalOption, timeRange)
        : base.interval,
      yAxisScales: isPersistedYAxisScales(parsed.yAxisScales) ? parsed.yAxisScales : base.yAxisScales,
    };
  } catch {
    return base;
  }
}

// ============================================================================
// Reducer
// ============================================================================

function toggle(set: ReadonlySet<string>, key: string): Set<string> {
  const next = new Set(set);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
}

function historianReducer(state: HistorianState, action: HistorianAction): HistorianState {
  switch (action.type) {
    case 'SITES_LOADING':
      return { ...state, sitesLoading: true, sitesError: null };

    case 'SITES_SUCCESS':
      return { ...state, sitesLoading: false, sites: action.payload, sitesError: null };

    case 'SITES_ERROR':
      return { ...state, sitesLoading: false, sitesError: action.payload };

    case 'CATALOG_LOADING': {
      const catalogLoading = new Set(state.catalogLoading).add(action.payload);
      const catalogErrors = new Map(state.catalogErrors);
      catalogErrors.delete(action.payload);
      return { ...state, catalogLoading, catalogErrors };
    }

    case 'CATALOG_SUCCESS': {
      const catalogs = new Map(state.catalogs).set(action.payload.siteId, action.payload.catalog);
      const catalogLoading = new Set(state.catalogLoading);
      catalogLoading.delete(action.payload.siteId);
      return { ...state, catalogs, catalogLoading };
    }

    case 'CATALOG_ERROR': {
      const catalogErrors = new Map(state.catalogErrors).set(action.payload.siteId, action.payload.message);
      const catalogLoading = new Set(state.catalogLoading);
      catalogLoading.delete(action.payload.siteId);
      return { ...state, catalogErrors, catalogLoading };
    }

    /**
     * ⚠️ The cap, the dedup, the alias, the colour and the axis are ALL decided
     * here rather than in the `addVariable` callback, and that is not a style
     * choice. The callback closes over a `state` snapshot, so a tree that adds
     * several metrics in one click — or any batched React update — runs every
     * call against the SAME pre-batch state: a callback-side cap sees length 1
     * twenty times over, and a callback-side colour picker hands all twenty the
     * same colour. The reducer is the only place that sees each predecessor.
     */
    case 'ADD_VARIABLE': {
      if (state.selectedVariables.length >= HISTORIAN_MAX_SERIES) return state;
      const id = variableId(action.payload.siteId, action.payload.deviceId, action.payload.metric);
      if (state.selectedVariables.some((v) => v.id === id)) return state;
      return {
        ...state,
        selectedVariables: [...state.selectedVariables, buildVariable(action.payload, state.selectedVariables)],
      };
    }

    // See the action's own comment: verbatim replacement, cap enforced here
    // exactly like `ADD_VARIABLE` — a seed is still untrusted input.
    //
    // ⚠️ Dedup by id BEFORE the cap (M-4, #806 review). Two rows sharing
    // one `id` share one React `key` (`SelectedVariablesPanel`) and one
    // `REMOVE_VARIABLE` filter, so removing either one removes BOTH — not
    // cosmetic. Dedup keeps the FIRST occurrence, matching `ADD_VARIABLE`'s
    // first-one-wins rule.
    //
    // ⚠️ BELT-AND-BRACES, not the primary defense (N-1, #806 review round 3)
    // — every `seedFromUrl` branch now dedups where it BUILDS its
    // `variables` array (a repeated `series=` entry, or legacy
    // `device_ids=GEN-1,GEN-1` / `metrics=kw,kw`, neither of which
    // `parseCsv` dedups) and skips a duplicate WITHOUT spending a cap slot
    // on it. Dedup ONLY here — after the caller had already capped on the
    // raw, undeduped array — is exactly what let a run of duplicates crowd
    // out later, genuinely distinct series before this ever ran; fixing it
    // at the SOURCE is what makes this reducer step and each branch's own
    // loop-cap agree again for every input, not just duplicate-free ones.
    // This case stays as a backstop for any future `SEED_VARIABLES`
    // dispatcher that forgets to dedup upstream.
    case 'SEED_VARIABLES': {
      const seen = new Set<string>();
      const deduped = action.payload.filter((v) => {
        if (seen.has(v.id)) return false;
        seen.add(v.id);
        return true;
      });
      return { ...state, selectedVariables: deduped.slice(0, HISTORIAN_MAX_SERIES) };
    }

    case 'REMOVE_VARIABLE':
      return {
        ...state,
        selectedVariables: state.selectedVariables.filter((v) => v.id !== action.payload),
      };

    case 'CLEAR_VARIABLES':
      return { ...state, selectedVariables: [] };

    /**
     * An operator-typed alias is sanitized and de-duplicated on the way in.
     * The server rejects the WHOLE query on one bad alias, and a duplicate
     * alias makes one series silently overwrite another in the pivoted row —
     * two coloured lines carrying identical values, with no error anywhere.
     * The variable's own current alias is excluded from `taken`, so re-saving
     * an unchanged name does not accrete `_2` suffixes.
     */
    case 'UPDATE_VARIABLE_ALIAS': {
      const target = state.selectedVariables.find((v) => v.id === action.payload.id);
      if (!target) return state;
      const taken = new Set(
        state.selectedVariables.filter((v) => v.id !== action.payload.id).map((v) => v.alias)
      );
      const alias = resolveUniqueAlias(sanitizeAlias(action.payload.alias), taken);
      return {
        ...state,
        selectedVariables: state.selectedVariables.map((v) =>
          v.id === action.payload.id ? { ...v, alias } : v
        ),
      };
    }

    case 'UPDATE_VARIABLE_COLOR':
      return {
        ...state,
        selectedVariables: state.selectedVariables.map((v) =>
          v.id === action.payload.id ? { ...v, color: action.payload.color } : v
        ),
      };

    /**
     * Back-filling a unit MUST re-derive the axis. A variable added with a null
     * unit lands on `left` by default; learning it is kW without moving it to
     * the power axis would plot kW against a percentage scale and look like a
     * flat line at zero.
     */
    case 'UPDATE_VARIABLE_UNIT':
      return {
        ...state,
        selectedVariables: state.selectedVariables.map((v) =>
          v.id === action.payload.id
            ? { ...v, unit: action.payload.unit, yAxisId: getYAxisForUnit(action.payload.unit) }
            : v
        ),
      };

    case 'TOGGLE_SITE':
      return { ...state, expandedSites: toggle(state.expandedSites, action.payload) };

    case 'TOGGLE_DEVICE':
      return { ...state, expandedDevices: toggle(state.expandedDevices, action.payload) };

    case 'TOGGLE_GROUP':
      return { ...state, toggledGroups: toggle(state.toggledGroups, action.payload) };

    case 'SET_SEARCH_QUERY':
      return { ...state, searchQuery: action.payload };

    case 'SET_TIME_RANGE':
      return { ...state, timeRange: action.payload, interval: coerceInterval(state.interval, action.payload) };

    case 'SET_INTERVAL':
      return { ...state, interval: coerceInterval(action.payload, state.timeRange) };

    case 'SET_Y_AXIS_SCALE':
      return {
        ...state,
        yAxisScales: { ...state.yAxisScales, [action.payload.axis]: action.payload.setting },
      };

    case 'QUERY_LOADING':
      return { ...state, queryLoading: true, queryError: null };

    case 'QUERY_SUCCESS':
      return { ...state, queryLoading: false, queryResult: action.payload, queryError: null };

    case 'QUERY_ERROR':
      return { ...state, queryLoading: false, queryError: action.payload };

    case 'CLEAR_QUERY':
      return { ...state, queryResult: null, queryError: null };

    default:
      return state;
  }
}

// ============================================================================
// Context
// ============================================================================

export interface HistorianContextType {
  state: HistorianState;

  loadSites: () => Promise<HistorianSite[]>;
  loadCatalog: (siteId: string) => Promise<HistorianCatalog | undefined>;

  toggleSite: (siteId: string) => void;
  toggleDevice: (key: string) => void;
  toggleGroup: (key: string) => void;
  setSearchQuery: (q: string) => void;

  addVariable: (v: AddVariableRequest) => void;
  removeVariable: (id: string) => void;
  clearVariables: () => void;
  updateVariableAlias: (id: string, alias: string) => void;
  updateVariableColor: (id: string, color: string) => void;
  canAddVariable: () => boolean;
  isSelected: (siteId: string, deviceId: string, metric: string) => boolean;

  setTimeRange: (r: TimeRange) => void;
  setTimeRangePreset: (p: TimeRangePreset) => void;
  setInterval: (i: IntervalOption) => void;
  setYAxisScale: (axis: 'left' | 'right' | 'right2', s: YAxisScaleSetting) => void;

  executeQuery: () => Promise<void>;
  clearQuery: () => void;

  seedFromUrl: (seed: HistorianSeed | HistorianSeriesSeed | null) => Promise<void>;
}

const HistorianContext = createContext<HistorianContextType | undefined>(undefined);

// ============================================================================
// Provider
// ============================================================================

/** Shape of one row from `GET /v1/sites`, as much of it as this page reads. */
interface SitesApiRow {
  site_id: string;
  name: string;
  site_code: string;
}

interface SitesApiResponse {
  success: boolean;
  data: {
    sites: SitesApiRow[];
    pagination?: { total: number; page: number; limit: number; total_pages: number };
  };
}

/**
 * ⚠️ The server CLAMPS `?limit` to 100 (`Math.min(100, ...)` in
 * `backend/src/routes/sites.routes.ts`). Asking for 500 is not an error and
 * returns no warning — it just returns the first 100 rows. A tenant past that
 * size would lose sites from the variable tree with nothing, anywhere, to say
 * so. So: ask for exactly the clamp and follow `total_pages`. At today's fleet
 * size that is a single request; the loop is insurance against a failure mode
 * that is invisible by construction.
 */
const SITES_PAGE_LIMIT = 100;

export interface HistorianProviderProps {
  children: ReactNode;
}

export const HistorianProvider: React.FC<HistorianProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(historianReducer, undefined, loadPersistedState);

  /**
   * The latest committed state, for callbacks that must NOT change identity.
   *
   * ⚠️ `executeQuery` cannot depend on `state.timeRange` or
   * `state.selectedVariables`, because executing a query CHANGES both: it
   * re-derives the preset window into `SET_TIME_RANGE`, and the unit back-fill
   * replaces `selectedVariables`. With those in its dependency array the
   * callback got a fresh identity on every run, so the obvious consumer —
   * `useEffect(run, [executeQuery])` — re-fired on each execute and looped,
   * pegging the CPU and hammering the API. genieos does not have this problem
   * because it never writes back the range it queried; this is new to the
   * port, and Task 13 is about to mount panels that will plausibly depend on
   * `executeQuery`.
   *
   * Reading through a ref keeps re-deriving the preset at execute time — which
   * is REQUIRED, a stored `last_1h` means "the hour before execute" — while
   * leaving the callback identity fixed for the provider's lifetime.
   *
   * ⚠️ `useLayoutEffect`, NOT `useEffect`, and this is not a preference.
   * Passive effects flush CHILD-BEFORE-PARENT, so a child's
   * `useEffect(…, [state.selectedVariables, executeQuery])` would run before
   * this provider's passive effect and `executeQuery` would read the PREVIOUS
   * commit — seeing an empty selection and returning early. Measured: that
   * shape fired zero queries, a silent no-op, which is worse than the loop it
   * replaced because nothing at all reports it. Layout effects all flush before
   * any passive effect, so this ordering holds for every descendant.
   *
   * Still an effect rather than a render-phase assignment, so an abandoned
   * concurrent render cannot leave the ref pointing at state that was never
   * committed.
   */
  const stateRef = useRef(state);
  useLayoutEffect(() => {
    stateRef.current = state;
  });

  /**
   * In-flight guards held in refs, not read off `state`.
   *
   * A `state`-based guard is a stale closure: two `loadCatalog('S-H')` calls in
   * one render pass both see an empty `catalogLoading` and both fire. A ref
   * mutates synchronously, so the second call sees the first.
   */
  /**
   * ⚠️ These hold the IN-FLIGHT PROMISE, not a boolean/Set flag (M-3, #806
   * review). Under the old flag shape, a concurrent caller got a SENTINEL —
   * `loadSites` returned `[]`, `loadCatalog` returned `undefined` — which was
   * harmless when both functions returned `void` and every caller read
   * `state` instead. Once they were widened to return their fetched value
   * directly (so `seedFromUrl` could use it without a `stateRef` round trip),
   * the sentinel became indistinguishable from "no sites" / "catalog
   * failed" to any concurrent caller. `VariableBrowser`'s mount effect calls
   * `loadSites()` on its own, and passive effects flush child-before-parent,
   * so a page-level `seedFromUrl` mount effect races it routinely — a race
   * measured to leave `siteName` permanently falling back to the raw
   * `siteId` (a UUID, in production) for the rest of the session. Caching
   * the promise means a concurrent call JOINS the existing fetch and gets
   * the real, eventual value instead of a fake "nothing here" answer.
   */
  const sitesInFlight = useRef<Promise<HistorianSite[]> | null>(null);
  const catalogsInFlight = useRef<Map<string, Promise<HistorianCatalog | undefined>>>(new Map());

  /**
   * Monotonic query generation — the interlock against a stale response.
   *
   * ⚠️ Every resolved `runSeriesQuery` used to dispatch unconditionally, so two
   * overlapping executes let the OLDER-but-slower response land last, leaving
   * `queryResult` describing a window `state.timeRange` no longer holds. That
   * nothing overlaps today is a property of the CONTROLS, not of this module:
   * the buttons disable while loading, but the assistant path, a keyboard
   * shortcut and `seedFromUrl` are not bound by that convention.
   *
   * Deliberately NOT keyed on `queryLoading` — that is the same UI convention
   * one layer down, and it is state this callback cannot read synchronously
   * anyway. A counter is self-contained and cannot be accidentally satisfied.
   */
  const queryGeneration = useRef(0);

  /**
   * Persist the operator's working set so a refresh or a tab-switch does not
   * discard it. Wrapped because the write can throw (see `loadPersistedState`);
   * a failed write costs the restore, never the page.
   *
   * `queryResult` is deliberately NOT persisted — it is unbounded (up to the
   * server's row cap × 12 series), would blow the ~5 MB quota, and re-running
   * the query is cheap and gives fresher data.
   */
  useEffect(() => {
    try {
      const payload: PersistedState = {
        selectedVariables: state.selectedVariables,
        timeRange: {
          preset: state.timeRange.preset,
          start: state.timeRange.start.toISOString(),
          end: state.timeRange.end.toISOString(),
        },
        interval: state.interval,
        yAxisScales: state.yAxisScales,
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {
      // Storage unavailable or over quota — the page keeps working.
    }
  }, [state.selectedVariables, state.timeRange, state.interval, state.yAxisScales]);

  /**
   * ⚠️ Returns the fetched sites, not just `void` — `seedFromUrl` needs the
   * value SYNCHRONOUSLY USABLE the moment this resolves, and `stateRef`
   * cannot provide that: a dispatch made from inside a promise callback (as
   * opposed to a React event handler) does not commit — and therefore does
   * not flush the `useLayoutEffect` that keeps `stateRef` current — until
   * the whole call stack unwinds back to the scheduler. Reading
   * `stateRef.current.sites` immediately after `await loadSites()` observed
   * the PRE-call value every time, empty on first load. Existing callers
   * (`VariableBrowser`) call this as `void loadSites()` and are unaffected.
   */
  const loadSites = useCallback(async (): Promise<HistorianSite[]> => {
    const inFlight = sitesInFlight.current;
    if (inFlight) return inFlight;

    const promise = (async (): Promise<HistorianSite[]> => {
      dispatch({ type: 'SITES_LOADING' });
      try {
        const collected: HistorianSite[] = [];
        let page = 1;
        let totalPages = 1;

        do {
          const res = await api.get<SitesApiResponse>(`/v1/sites?limit=${SITES_PAGE_LIMIT}&page=${page}`);
          const body = res.data.data;
          for (const s of body.sites ?? []) {
            collected.push({ siteId: s.site_id, siteName: s.name, siteCode: s.site_code });
          }
          // Absent pagination (older response, or a stub) means "this is all of it".
          totalPages = body.pagination?.total_pages ?? 1;
          page += 1;
        } while (page <= totalPages);

        dispatch({ type: 'SITES_SUCCESS', payload: collected });
        return collected;
      } catch (err) {
        dispatch({
          type: 'SITES_ERROR',
          payload: err instanceof Error ? err.message : 'Failed to load sites',
        });
        return [];
      } finally {
        // Cleared regardless of outcome, so a caller AFTER this one — e.g. a
        // user-driven retry once `sitesError` is showing — genuinely re-fetches
        // rather than joining a settled promise forever.
        sitesInFlight.current = null;
      }
    })();

    sitesInFlight.current = promise;
    return promise;
  }, []);

  /**
   * ⚠️ Returns the fetched (or already-cached) catalog rather than `void`,
   * for the same reason as `loadSites` above — a caller in the same async
   * chain as the dispatch cannot observe it via `stateRef` in time.
   * A concurrent call for the SAME site joins the in-flight promise rather
   * than returning early, so `undefined` means exactly one thing: the fetch
   * genuinely failed (and `CATALOG_ERROR` was dispatched for that site).
   * ⚠️ It used to also mean "another call is already fetching" — that sentinel
   * was the M-3 defect: indistinguishable from failure, and reached routinely,
   * because `VariableBrowser`'s mount effect calls `loadSites()` and passive
   * effects flush child before parent. Do not reintroduce an early return here.
   */
  const loadCatalog = useCallback(
    async (siteId: string): Promise<HistorianCatalog | undefined> => {
      const inFlight = catalogsInFlight.current.get(siteId);
      if (inFlight) return inFlight;
      const cached = stateRef.current.catalogs.get(siteId);
      if (cached) return cached;

      const promise = (async (): Promise<HistorianCatalog | undefined> => {
        dispatch({ type: 'CATALOG_LOADING', payload: siteId });
        try {
          const catalog = await fetchCatalog(siteId);
          dispatch({ type: 'CATALOG_SUCCESS', payload: { siteId, catalog } });
          return catalog;
        } catch (err) {
          // Per-site: one site's catalog failing must not blank the whole tree.
          dispatch({
            type: 'CATALOG_ERROR',
            payload: {
              siteId,
              message: err instanceof Error ? err.message : 'Failed to load catalog',
            },
          });
          return undefined;
        } finally {
          // Cleared regardless of outcome — see `loadSites`' identical comment.
          catalogsInFlight.current.delete(siteId);
        }
      })();

      catalogsInFlight.current.set(siteId, promise);
      return promise;
    },
    // Stable for the same reason as `executeQuery`: loading a catalog replaces
    // `state.catalogs`, so depending on it would hand every caller a fresh
    // identity the moment the call succeeded.
    []
  );

  const toggleSite = useCallback((siteId: string) => {
    dispatch({ type: 'TOGGLE_SITE', payload: siteId });
  }, []);

  const toggleDevice = useCallback((key: string) => {
    dispatch({ type: 'TOGGLE_DEVICE', payload: key });
  }, []);

  const toggleGroup = useCallback((key: string) => {
    dispatch({ type: 'TOGGLE_GROUP', payload: key });
  }, []);

  const setSearchQuery = useCallback((q: string) => {
    dispatch({ type: 'SET_SEARCH_QUERY', payload: q });
  }, []);

  const addVariable = useCallback((v: AddVariableRequest) => {
    dispatch({ type: 'ADD_VARIABLE', payload: v });
  }, []);

  const removeVariable = useCallback((id: string) => {
    dispatch({ type: 'REMOVE_VARIABLE', payload: id });
  }, []);

  /**
   * Clearing the selection also clears the result. This matches genieos, and it
   * is deliberate rather than incidental: `queryResult.data` is keyed by the
   * aliases of the variables just removed, so keeping it would leave the chart
   * and table rendering series that no longer exist in the panel beside them —
   * with no way to remove them short of running another query.
   */
  const clearVariables = useCallback(() => {
    dispatch({ type: 'CLEAR_VARIABLES' });
    dispatch({ type: 'CLEAR_QUERY' });
  }, []);

  const updateVariableAlias = useCallback((id: string, alias: string) => {
    dispatch({ type: 'UPDATE_VARIABLE_ALIAS', payload: { id, alias } });
  }, []);

  const updateVariableColor = useCallback((id: string, color: string) => {
    dispatch({ type: 'UPDATE_VARIABLE_COLOR', payload: { id, color } });
  }, []);

  const canAddVariable = useCallback(
    () => state.selectedVariables.length < HISTORIAN_MAX_SERIES,
    [state.selectedVariables.length]
  );

  const isSelected = useCallback(
    (siteId: string, deviceId: string, metric: string) =>
      state.selectedVariables.some((v) => v.id === variableId(siteId, deviceId, metric)),
    [state.selectedVariables]
  );

  const setTimeRange = useCallback((r: TimeRange) => {
    dispatch({ type: 'SET_TIME_RANGE', payload: r });
  }, []);

  const setTimeRangePreset = useCallback((p: TimeRangePreset) => {
    // `custom` carries no duration — it needs an explicit start/end via setTimeRange.
    if (p === 'custom') return;
    const preset = TIME_RANGE_PRESETS.find((x) => x.id === p);
    if (!preset) return;
    const end = new Date();
    dispatch({
      type: 'SET_TIME_RANGE',
      payload: { preset: p, start: new Date(end.getTime() - preset.ms), end },
    });
  }, []);

  const setInterval = useCallback((i: IntervalOption) => {
    dispatch({ type: 'SET_INTERVAL', payload: i });
  }, []);

  const setYAxisScale = useCallback((axis: 'left' | 'right' | 'right2', s: YAxisScaleSetting) => {
    dispatch({ type: 'SET_Y_AXIS_SCALE', payload: { axis, setting: s } });
  }, []);

  /**
   * The core of `executeQuery`, parameterized rather than reading `stateRef`
   * — so `seedFromUrl` can run a query against variables/range/interval it
   * just computed LOCALLY, without a round trip through dispatched state.
   *
   * ⚠️ Why the round trip doesn't work: a `dispatch` made from inside a
   * promise callback (as opposed to a React event handler) does not commit
   * — and therefore does not flush the `useLayoutEffect` that keeps
   * `stateRef` current — until the whole call stack unwinds back to the
   * scheduler. `seedFromUrl` dispatches `SEED_VARIABLES` and then, in the
   * SAME async call chain, would need `executeQuery` to see that dispatch
   * via `stateRef.current.selectedVariables` — which is still the PRE-seed
   * value at that point. Measured: `runSeriesQuery` was called zero times
   * from `seedFromUrl` before this was pulled out. Passing the variables in
   * directly sidesteps the read entirely.
   *
   * `executeQuery` itself is unaffected — it still reads `stateRef.current`
   * exactly as before, for every OTHER caller (buttons, keyboard shortcut),
   * none of which call it from the same tick as a dispatch they just made.
   */
  const runQueryFor = useCallback(
    async (variables: SelectedVariable[], timeRange: TimeRange, interval: IntervalOption) => {
      if (variables.length === 0) return;

      dispatch({ type: 'QUERY_LOADING' });

      const { start, end } = resolveExecutionRange(timeRange);
      const executed: TimeRange = { preset: timeRange.preset, start, end };

      /**
       * Reflect the window that actually ran, so the range control and the chart
       * axis agree with the data rather than with the stale stored boundaries —
       * but ONLY when it moved.
       *
       * ⚠️ This used to dispatch a fresh object unconditionally, which made
       * `state.timeRange` self-churn on every execute: any
       * `useEffect(…, [state.timeRange])` re-fired forever, to OOM. A `custom`
       * range resolves to its own stored boundaries by construction, so that
       * write-back was pure churn and is now skipped entirely.
       *
       * A duration preset genuinely resolves a NEW window as the clock advances,
       * so it still dispatches — that is a real data change, not churn, and
       * suppressing it would be the stale-window bug this re-derivation exists to
       * prevent.
       *
       * ⚠️ READ THIS BEFORE DEPENDING ON `state.timeRange`.
       *
       * `sameRange` suppresses only the SPURIOUS case — a window that resolved to
       * the same instants it already held. It does not, and cannot, suppress a
       * duration preset under a live clock: `last_1h` resolves a different window
       * every time it runs, so `state.timeRange`'s object identity changes once
       * per query BY DESIGN.
       *
       * Therefore an effect keyed on `state.timeRange`'s identity that also
       * triggers a query is STILL an unbounded loop, in the same way
       * `useEffect(() => setCount(c + 1), [count])` is. Key such an effect on
       * primitives instead — `state.timeRange.preset`, or
       * `state.timeRange.start.getTime()` — never on the object.
       *
       * A consumer that wants the window a result was actually computed over
       * should read `queryResult.metadata.start` / `.end`, which the server
       * returns and which changes only when a query completes.
       *
       * The cleaner design is for `state.timeRange` to hold the operator's INTENT
       * (preset, plus custom bounds) and for the resolved window to live only in
       * `queryResult.metadata` — considered and deliberately deferred during the
       * #806 rebuild, because Task 13's panels were already built against this
       * shape and the change ripples into the deep-link and export tasks.
       */
      if (!sameRange(timeRange, executed)) {
        dispatch({ type: 'SET_TIME_RANGE', payload: executed });
      }

      const spec: SeriesQuerySpec = {
        series: variables.map((v) => ({
          siteId: v.siteId,
          deviceId: v.deviceId,
          metric: v.metric,
          alias: v.alias,
        })),
        startTime: start.toISOString(),
        endTime: end.toISOString(),
        interval,
      };

      const generation = (queryGeneration.current += 1);

      try {
        const result = await runSeriesQuery(spec);
        // Superseded while in flight: drop it. Not an error, and deliberately no
        // dispatch at all — QUERY_LOADING stays true because the newer request
        // genuinely is still running.
        if (generation !== queryGeneration.current) return;

        dispatch({
          type: 'QUERY_SUCCESS',
          payload: {
            queryId: result.queryId,
            data: result.rows,
            columnMeta: result.columnMeta,
            metadata: {
              // The server returns one row per timestamp already downsampled, so
              // there is no pre-downsample count to report. Both fields carry
              // `rowCount` rather than inventing a ratio the server never sent.
              pointCount: result.metadata.rowCount,
              downsampledFrom: result.metadata.rowCount,
              queryTimeMs: result.metadata.queryTimeMs,
              interval: result.metadata.interval,
              truncated: result.metadata.truncated,
              truncatedSide: result.metadata.truncatedSide,
              completeFrom: result.metadata.completeFrom,
              dataThrough: result.metadata.dataThrough,
              rollupLagSeconds: result.metadata.rollupLagSeconds,
              start: result.metadata.start,
              end: result.metadata.end,
            },
          },
        });

        /**
         * Unit resolution, in order: the catalog (already on the variable),
         * then the response `columnMeta`, then the static `METRIC_UNITS` catalog.
         *
         * ⚠️ This chain is not a rare fallback, and which rung fires moved twice in
         * djin-cloud#832. The rollups do not materialize `tags`, so the rollup half of a
         * query still yields `unit: null` — but PR #845's raw TAIL FILL does select
         * `MAX(tags->>'unit')`, so a routed query resolves units whenever its window
         * reaches past the rollup checkpoint, which for a live view is always. And routing
         * is on by default now (`HISTORIAN_ROUTE_THROUGH_ROLLUPS`), so "Azure does not take
         * the rollup path" stopped being true once a tenant's Terraform is applied.
         * Keep every rung asserted rather than inferred from whichever one a given box
         * happens to take — that is exactly what went stale here.
         */
        for (const v of variables) {
          if (v.unit !== null) continue;
          const resolved = result.columnMeta[v.alias]?.unit ?? METRIC_UNITS[v.metric] ?? null;
          if (resolved !== null) {
            dispatch({ type: 'UPDATE_VARIABLE_UNIT', payload: { id: v.id, unit: resolved } });
          }
        }
      } catch (err) {
        // A superseded FAILURE must not blank a newer result the user is looking
        // at — the stale-response rule is about ordering, not about outcome.
        if (generation !== queryGeneration.current) return;
        dispatch({ type: 'QUERY_ERROR', payload: extractHistorianError(err) });
      }
    },
    // Intentionally EMPTY — every input is a parameter (`variables`, `timeRange`,
    // `interval`), not read from render-scope state, so there is nothing here
    // that legitimately varies across renders. This is the SAME invariant
    // `executeQuery` used to hold directly (see its own comment below): a
    // future dependency added here without checking it is genuinely stable
    // reintroduces the identity-churn re-render loop this extraction was
    // built to avoid, one level removed from where it would show up.
    []
  );

  const executeQuery = useCallback(async () => {
    // One snapshot for the whole run: the variables we queried for are the ones
    // whose units we back-fill afterwards, even if the selection changed in the
    // meantime.
    const snapshot = stateRef.current;
    await runQueryFor(snapshot.selectedVariables, snapshot.timeRange, snapshot.interval);
    // Intentionally EMPTY (aside from the always-stable `runQueryFor`) — every
    // other input is read from `stateRef`. See the ref's comment: a state
    // dependency here is a re-render loop for any consumer that does
    // `useEffect(run, [executeQuery])`.
  }, [runQueryFor]);

  const clearQuery = useCallback(() => {
    dispatch({ type: 'CLEAR_QUERY' });
  }, []);

  /**
   * Deep-link seeding — turns either URL shape from `historianUrlParams.ts`
   * into live selection + an executed (or replayed) query.
   *
   * ⚠️ ALWAYS REPLACES the selection (via `SEED_VARIABLES`), never appends —
   * consistent across all three shapes below, including the persisted-query
   * replay (which has no choice: it must reuse exact stored aliases). A
   * shared link should show exactly what it names, not merge onto whatever
   * was already selected from a previous session.
   *
   * ⚠️ Builds `SelectedVariable[]` locally via `buildVariable` (the same
   * function `ADD_VARIABLE` uses) instead of dispatching one `ADD_VARIABLE`
   * per entry, and runs the query via `runQueryFor` with that SAME local
   * list rather than calling `executeQuery()`. Both are consequences of one
   * fact: a `dispatch` made from inside a promise callback does not commit —
   * and therefore does not update `stateRef` — until the whole call stack
   * unwinds back to the scheduler. Within this one call, nothing dispatched
   * earlier in the SAME call is visible through `state` or `stateRef` later
   * in the call. `loadSites`/`loadCatalog` sidestep this by returning their
   * fetched values directly (see their own comments); this is the same
   * pattern applied to variable construction and query execution.
   *
   * The legacy and explicit-series branches read
   * `stateRef.current.timeRange`/`.interval` exactly once, via
   * `resolveRangeAndInterval`, to resolve the "keep current" case — safe
   * because nothing in THIS call has dispatched anything yet at that point.
   * ⚠️ The persisted-query replay branch does NOT call
   * `resolveRangeAndInterval`: it has a canonical range and interval from
   * `result.metadata` (the server's own record of what the stored query
   * actually ran over) and uses that instead. ⚠️ CORRECTION (N-5, #806
   * review round 3): this used to claim "there is nothing to fall back to
   * keeping" — false. Its `SET_TIME_RANGE` dispatch IS guarded by
   * `!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())` with
   * no `else`, so an unparseable `metadata.start`/`.end` DOES fall back to
   * keeping whatever range was already current — the round-2 M-5 test
   * exercises exactly that path. Do not add a `resolveRangeAndInterval`
   * call here expecting this comment's "each branch" to have meant
   * literally all three; the mechanism differs even though both branches
   * can end up keeping the current range.
   */
  const seedFromUrl = useCallback(
    async (seed: HistorianSeed | HistorianSeriesSeed | null): Promise<void> => {
      if (!seed) return;

      // Shared by both the legacy cross-product and the explicit series
      // list: a start/end pair applies only when BOTH parse, else the
      // CURRENT range is kept (read once, before this call has dispatched
      // anything). Always resolves to a valid IntervalOption — 'auto' when
      // the seed's interval is absent or outside the vocabulary.
      const resolveRangeAndInterval = (
        startIso: string | null,
        endIso: string | null,
        interval: string | null
      ): { timeRange: TimeRange; interval: IntervalOption } => {
        let timeRange = stateRef.current.timeRange;
        if (startIso && endIso) {
          const start = new Date(startIso);
          const end = new Date(endIso);
          if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
            timeRange = { preset: 'custom', start, end };
          }
        }
        return { timeRange, interval: isInterval(interval) ? interval : 'auto' };
      };

      // ----------------------------------------------------------------
      // Legacy shape: one site, a metrics × device-ids cross-product.
      // ----------------------------------------------------------------
      if ('siteId' in seed) {
        const sites = await loadSites();
        const catalog = await loadCatalog(seed.siteId);

        const variables: SelectedVariable[] = [];
        if (catalog) {
          const siteName = sites.find((s) => s.siteId === seed.siteId)?.siteName ?? seed.siteId;
          const deviceIds = seed.deviceIds.length > 0 ? seed.deviceIds : catalog.devices.map((d) => d.deviceId);

          // N-1 (#806 review): dedup HERE, where the array is built — not
          // just in the `SEED_VARIABLES` reducer. `seed.deviceIds`/
          // `seed.metrics` come straight from `parseCsv`, which does not
          // dedup, so `device_ids=GEN-1,GEN-1` or `metrics=kw,kw` can put
          // the SAME (device, metric) pair through this cross-product
          // twice. A duplicate is SKIPPED without counting toward the cap —
          // counting it was the actual bug: reducer-only dedup ran on an
          // array the loop's own cap had already truncated, so a run of
          // duplicates crowded out later, genuinely distinct series before
          // dedup ever got a chance to run.
          const seenIds = new Set<string>();
          seedLoop: for (const deviceId of deviceIds) {
            const device = catalog.devices.find((d) => d.deviceId === deviceId);
            if (!device) continue;
            for (const metric of seed.metrics) {
              const catalogMetric = device.metrics.find((m) => m.name === metric);
              if (!catalogMetric) continue;
              const id = variableId(seed.siteId, deviceId, metric);
              if (seenIds.has(id)) continue;
              if (variables.length >= HISTORIAN_MAX_SERIES) break seedLoop;
              seenIds.add(id);
              variables.push(
                buildVariable(
                  {
                    siteId: seed.siteId,
                    siteName,
                    deviceId,
                    deviceLabel: device.displayName ?? deviceId,
                    metric,
                    unit: catalogMetric.unit,
                  },
                  variables
                )
              );
            }
          }
        }

        dispatch({ type: 'SEED_VARIABLES', payload: variables });
        // H-2 (#806 review): a seed that resolves to ZERO variables (catalog
        // fetch failed, or no device/metric in it matched) still just
        // replaced the selection with `[]` — and `runQueryFor` below returns
        // immediately on an empty list without dispatching anything, so a
        // PRIOR `queryResult` was left standing, describing a selection that
        // no longer exists. This is exactly the state `clearVariables`'s own
        // comment says must never happen. Clearing unconditionally (not just
        // when `variables.length === 0`) matches `clearVariables`'s own
        // replace-then-clear order and costs nothing in the non-empty case —
        // `runQueryFor` immediately dispatches a fresh `QUERY_SUCCESS`/
        // `QUERY_ERROR` over it.
        dispatch({ type: 'CLEAR_QUERY' });
        const { timeRange, interval } = resolveRangeAndInterval(seed.startIso, seed.endIso, seed.interval);
        dispatch({ type: 'SET_TIME_RANGE', payload: timeRange });
        dispatch({ type: 'SET_INTERVAL', payload: interval });
        await runQueryFor(variables, timeRange, interval);
        return;
      }

      // ----------------------------------------------------------------
      // Series shape, no explicit series: replay a persisted query by id.
      //
      // Dispatches QUERY_SUCCESS directly rather than running a fresh query
      // — a fresh execute would regenerate aliases via `buildVariable`, and
      // those are NOT guaranteed to match the aliases already baked into
      // this result's `rows` / `columnMeta` keys (the query may have been
      // run with a renamed alias, or a different site-name abbreviation).
      // Replaying must reuse the EXACT aliases the stored result carries.
      // ----------------------------------------------------------------
      if (seed.series.length === 0) {
        if (!seed.queryId) return;

        // M-5 (#806 review): this branch used to dispatch `QUERY_SUCCESS`
        // with no `QUERY_LOADING` and no `queryGeneration` participation at
        // all — the ref's own comment names this exact caller as one NOT
        // bound by the interlock convention, which made it a stated
        // invariant this code was violating, not a hypothetical. An
        // `executeQuery`/`runQueryFor` already in flight would land AFTER
        // this replay and silently overwrite it with rows keyed to
        // REGENERATED aliases, breaking the exact alias -> row-key linkage
        // this branch's own comment says must be preserved. Bumping first
        // means any such in-flight call is now the STALE one and drops its
        // own result on arrival instead.
        dispatch({ type: 'QUERY_LOADING' });
        const generation = (queryGeneration.current += 1);

        const sites = await loadSites();

        // H-1 (#806 review): `fetchPersistedQuery` is the ONLY unguarded
        // await in this function — `loadSites`/`loadCatalog`/`runQueryFor`
        // all catch internally, which is exactly why this one looked safe.
        // `GET /v1/historian/queries/:id` 404s for an unknown id or another
        // tenant's id — the documented, expected outcome for a `q=` link
        // that outlived the 90-day retention window or crossed a tenant
        // boundary, which is precisely what a SHARED link does. Unguarded,
        // that was an unhandled promise rejection and a blank page.
        let result: SeriesQueryResult;
        try {
          result = await fetchPersistedQuery(seed.queryId);
        } catch (err) {
          if (generation !== queryGeneration.current) return;
          dispatch({ type: 'QUERY_ERROR', payload: extractHistorianError(err) });
          return;
        }
        if (generation !== queryGeneration.current) return;

        // N-1 (#806 review): dedup + cap by loop, same as the other two
        // branches — `.slice(0, HISTORIAN_MAX_SERIES)` before mapping had
        // the identical bug: it counted duplicate ids toward the cap before
        // any dedup ran. Two `columnMeta` entries sharing an id is a
        // narrower case than the other branches (it needs a persisted spec
        // with the same site:device:metric under two different aliases,
        // which only a non-UI caller could have produced), but the
        // `SEED_VARIABLES` reducer's dedup would otherwise silently drop
        // one AFTER this loop already spent a cap slot on it.
        const colorsInUse = new Set<string>();
        const seenIds = new Set<string>();
        const variables: SelectedVariable[] = [];
        for (const [alias, meta] of Object.entries(result.columnMeta) as Array<
          [string, SeriesColumnMeta]
        >) {
          const id = variableId(meta.siteId, meta.deviceId, meta.metric);
          if (seenIds.has(id)) continue;
          if (variables.length >= HISTORIAN_MAX_SERIES) break;
          seenIds.add(id);
          const siteName = sites.find((s) => s.siteId === meta.siteId)?.siteName ?? meta.siteId;
          const color = nextColor(colorsInUse, variables.length);
          colorsInUse.add(color);
          variables.push({
            id,
            siteId: meta.siteId,
            siteName,
            deviceId: meta.deviceId,
            // The catalog isn't loaded on this path (only site names are
            // needed) — the device id stands in for its display name.
            deviceLabel: meta.deviceId,
            metric: meta.metric,
            alias,
            displayLabel: `${siteShort(siteName, meta.siteId)} · ${meta.deviceId} · ${meta.metric.replace(/_/g, ' ')}`,
            color,
            unit: meta.unit,
            yAxisId: getYAxisForUnit(meta.unit),
          });
        }

        dispatch({ type: 'SEED_VARIABLES', payload: variables });

        const start = new Date(result.metadata.start);
        const end = new Date(result.metadata.end);
        if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
          dispatch({ type: 'SET_TIME_RANGE', payload: { preset: 'custom', start, end } });
        }
        dispatch({
          type: 'SET_INTERVAL',
          payload: isInterval(result.metadata.interval) ? result.metadata.interval : 'auto',
        });
        dispatch({
          type: 'QUERY_SUCCESS',
          payload: {
            queryId: result.queryId,
            data: result.rows,
            columnMeta: result.columnMeta,
            metadata: {
              // Same "no pre-downsample count" reasoning as `runQueryFor`'s
              // own success handler — see its comment.
              pointCount: result.metadata.rowCount,
              downsampledFrom: result.metadata.rowCount,
              queryTimeMs: result.metadata.queryTimeMs,
              interval: result.metadata.interval,
              truncated: result.metadata.truncated,
              truncatedSide: result.metadata.truncatedSide,
              completeFrom: result.metadata.completeFrom,
              dataThrough: result.metadata.dataThrough,
              rollupLagSeconds: result.metadata.rollupLagSeconds,
              start: result.metadata.start,
              end: result.metadata.end,
            },
          },
        });
        return;
      }

      // ----------------------------------------------------------------
      // Series shape with an explicit series list: exact site:device:metric
      // triples, not a cross-product — build each verbatim and execute fresh.
      // ----------------------------------------------------------------
      const sites = await loadSites();
      const distinctSiteIds = Array.from(new Set(seed.series.map((s) => s.siteId)));
      const catalogsBySite = new Map<string, HistorianCatalog | undefined>();
      await Promise.all(
        distinctSiteIds.map(async (siteId) => {
          catalogsBySite.set(siteId, await loadCatalog(siteId));
        })
      );

      // N-1 (#806 review): dedup HERE too, same reason as the legacy
      // branch above — a hand-typed `series=` link can repeat a triple
      // (`writeHistorianUrl` never emits duplicates, but this branch reads
      // untrusted input), and skipping it must not count toward the cap.
      const variables: SelectedVariable[] = [];
      const seenIds = new Set<string>();
      seriesLoop: for (const item of seed.series) {
        const id = variableId(item.siteId, item.deviceId, item.metric);
        if (seenIds.has(id)) continue;
        if (variables.length >= HISTORIAN_MAX_SERIES) break seriesLoop;
        seenIds.add(id);
        const catalog = catalogsBySite.get(item.siteId);
        const device = catalog?.devices.find((d) => d.deviceId === item.deviceId);
        const catalogMetric = device?.metrics.find((m) => m.name === item.metric);
        const siteName = sites.find((s) => s.siteId === item.siteId)?.siteName ?? item.siteId;
        variables.push(
          buildVariable(
            {
              siteId: item.siteId,
              siteName,
              deviceId: item.deviceId,
              deviceLabel: device?.displayName ?? item.deviceId,
              metric: item.metric,
              unit: catalogMetric?.unit ?? null,
            },
            variables
          )
        );
      }

      dispatch({ type: 'SEED_VARIABLES', payload: variables });
      // See the legacy branch's identical dispatch above (H-2) — this branch
      // cannot actually reach an empty `variables` today (a non-empty
      // `seed.series` always pushes at least one entry before the cap can
      // bite), but clearing unconditionally keeps both branches honouring
      // the same invariant rather than one relying on "can't happen".
      dispatch({ type: 'CLEAR_QUERY' });
      const { timeRange, interval } = resolveRangeAndInterval(seed.startIso, seed.endIso, seed.interval);
      dispatch({ type: 'SET_TIME_RANGE', payload: timeRange });
      dispatch({ type: 'SET_INTERVAL', payload: interval });
      await runQueryFor(variables, timeRange, interval);
    },
    [loadSites, loadCatalog, runQueryFor]
  );

  const contextValue = useMemo<HistorianContextType>(
    () => ({
      state,
      loadSites,
      loadCatalog,
      toggleSite,
      toggleDevice,
      toggleGroup,
      setSearchQuery,
      addVariable,
      removeVariable,
      clearVariables,
      updateVariableAlias,
      updateVariableColor,
      canAddVariable,
      isSelected,
      setTimeRange,
      setTimeRangePreset,
      setInterval,
      setYAxisScale,
      executeQuery,
      clearQuery,
      seedFromUrl,
    }),
    [
      state,
      loadSites,
      loadCatalog,
      toggleSite,
      toggleDevice,
      toggleGroup,
      setSearchQuery,
      addVariable,
      removeVariable,
      clearVariables,
      updateVariableAlias,
      updateVariableColor,
      canAddVariable,
      isSelected,
      setTimeRange,
      setTimeRangePreset,
      setInterval,
      setYAxisScale,
      executeQuery,
      clearQuery,
      seedFromUrl,
    ]
  );

  return <HistorianContext.Provider value={contextValue}>{children}</HistorianContext.Provider>;
};

// ============================================================================
// Hook
// ============================================================================

export const useHistorian = (): HistorianContextType => {
  const context = useContext(HistorianContext);
  if (context === undefined) {
    throw new Error('useHistorian must be used within a HistorianProvider');
  }
  return context;
};
